/* prettier-ignore */

import { Inject, Injectable, signal } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { RestService } from "./rest.service";
import { ChatBotScriptService } from "./chatbot.service";
import { BehaviorSubject, combineLatest, filter, firstValueFrom, Observable, take } from "rxjs";
import { DOCUMENT } from "@angular/common";
import { GoogleTranslateService } from "./google-translate.service";

@Injectable({
  providedIn: "root",
})

export class ToolsService {
    private isAppSubject = new BehaviorSubject<boolean | null>(null);
    private isDistributorSubject = new BehaviorSubject<boolean | null>(null);
    isApp$ = this.isAppSubject.asObservable();
    isDistributor$ = this.isDistributorSubject.asObservable();

    ttlTwoHours: number = 1000 * 3600 * 2; //2horas
    homeDistributor: any = null;
    menuIsShown: boolean = false;
    showMenuDropdown: any;

    enabledInstallBanner = signal(true);
    showRouteSpinner = signal(false);
    showHeader = signal(false);
    languages = signal([]);

    isPreProduction = signal(false);
    isCorporativeColorsLoaded = signal(false);
    isAppLoaded = signal(false);
    isTechEditMode = signal(false);
    isTechMode = signal(false);
    isRegisterMode = signal(false);
    isInfoRoutePage = signal(false);

    isWebApp = signal(false);
    chatType = signal('text');

    headerLangType = signal('flags');

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta,
    private titleService: Title,
    public translate: TranslateService,
    private rest: RestService,
    private chatService: ChatBotScriptService,
    public googleTranslateService: GoogleTranslateService,
  ) {}

  public waitUntilReady(): Promise<[boolean | null, boolean | null]> {
    return firstValueFrom(
      combineLatest([this.isApp$, this.isDistributor$]).pipe(
        filter(([isApp, isDistributor]) => isApp !== null || isDistributor !== null),
        take(1),
      )
    );
  }

  enableSpinner(){
    this.showRouteSpinner.update(prev => true)
  }

  disableSpinner(){
    this.showRouteSpinner.update(prev => false)
  }

  enableHeader(){
    this.showHeader.update(prev => true);
  }

  disableHeader(){
    this.showHeader.update(prev => false);
  }

  setIsApp(value: boolean) {
    // this.setAppStarted();
    this.isAppSubject.next(value);
  }

  setIsDistributor(value: boolean) {
    // this.setAppStarted();
    this.isDistributorSubject.next(value);
  }

  setAppStarted(){
    this.setCorporativeColors();
    this.enableHeader();
    this.isAppLoaded.set(true)
  }

  setCorporativeColors(){
    this.setCorporiveColors();
    this.changeThemeColor(this.rest?.device?.style?.colorCorp1 || "#000");
    this.isCorporativeColorsLoaded.update(prev => true);
  }

  get isApp(): boolean | null {
    return this.isAppSubject.value;
  }

  get isDistributor(): boolean | null {
    return this.isDistributorSubject.value;
  }

  getDistributorUrl(name) {
    let url = new URL(window.location.href);
    let parts = url.hostname.split(".");
    parts.shift();
    url.hostname = parts.join(".");
    url.hostname = `${name}.${url.hostname}`;
    url.pathname = url.pathname.substring(0, url.pathname.lastIndexOf("/"));
    url.search = "";
    return url.toString();
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //------------------------------------------------------------ TEMP LOCAL STORAGE ------------------------------------------------------------------  
  setWithExpiry(key, value, ttl = this.ttlTwoHours) {
    const now = new Date();
    const item = { value: value, expiry: now.getTime() + ttl };
    localStorage.setItem(key, JSON.stringify(item));
  }
  getWithExpiry(key) {
    const itemStr = localStorage?.getItem(key);
    if (!itemStr) 
      return null;
    
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- SLUGFUNCTION ---------------------------------------------------------------------
  slugify(str) {
    return str
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- ERROR IMAGEN ----------------------------------------------------------------------
  onImageError(e) {
    e.target.src = "/assets/imgs/imageDefault.png";
  }
  onImgOfferError(e) {
    e.target.style.objectFit = "contain";
    e.target.src = "/assets/imgs/offerDefault.webp";
  }
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- SET SEO PAGE ---------------------------------------------------------------------
  public setMetaDescription(content: string) {
    this.meta.updateTag({ name: "description", content: content });
  }
  public setMetaTitle(title: string) {
    this.titleService.setTitle(title);
  }

  setCorporiveColors() {
    const root = document.querySelector(":root") as HTMLElement;
    root.style.setProperty("--main-color", this.rest?.device?.style?.colorCorp1 || "#00ABAF");
    root.style.setProperty("--main-color-alpha", `${this.rest?.device?.style?.colorCorp1}66` || "#00ABAF66");
    root.style.setProperty("--main-color-strong", this.rest?.device?.style?.colorCorp2 || "#08515D");
    root.style.setProperty("--header-color", this.rest?.device?.style?.headerColor || "#00ABAF");
    root.style.setProperty("--compl-color", this.rest?.device?.style?.corpCompl || "#08515D");
    root.style.setProperty("--background-color", this.rest?.device?.style?.backgroundColor || "#FFF");
    
    // New corporative colours
    root.style.setProperty("--primary", this.rest?.device?.style?.colorCorp1 || "#00ABAF");
    root.style.setProperty("--primary-dark", "color-mix(in srgb, var(--primary) 40%, black 60%)");
    root.style.setProperty("--primary-light", "color-mix(in srgb, var(--primary) 70%, white 30%)");
    root.style.setProperty("--secondary", this.rest?.device?.style?.colorCorp2 || "#08515D");
    root.style.setProperty("--secondary-dark", "color-mix(in srgb, var(--secondary) 40%, black 60%)");
    root.style.setProperty("--secondary-light", "color-mix(in srgb, var(--secondary) 70%, white 30%)");

    this.isWebApp()
      ? root.style.setProperty("--drag-delay", '2s')
      : root.style.setProperty("--drag-delay", '0.15s')
  }

  changeLanguage(lang){
    // const cookieLang = this.googleTranslateService.getCookieLang();
    // if (cookieLang) this.googleTranslateService.deleteCookieLang();
    // this.googleTranslateService.currentLang = lang;
    // this.googleTranslateService.setLocalLang(lang);
    this.chatService.setLang(lang);
    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
  }

  setLanguages(langs){
    this.languages.set(langs?.map((langCode) =>  ({ code: langCode, img: `/assets/langs/${langCode}.svg` }) ) || [])
  }

  getLocation() {
    return new Observable((observer) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => observer.next(position.coords),
          (error) => observer.error(error),
          { enableHighAccuracy: true }
        );
      } else observer.error("La geolocalización no está disponible en este navegador.");
    });
  }

  getSubdomain(url) {
    let domain = url;
    if (url.includes("://")) domain = url.split("://")[1];
    const subdomain = domain.split(".");
    let urlSegments = 2;
    if (domain.includes("localhost")) 
      urlSegments = 1;
  
    return subdomain.length > urlSegments ? subdomain[0] : null;
  }

  changeThemeColor(color: string) {
    this.document.querySelector('meta[name="theme-color"]').setAttribute("content", color);
  }

  toggleMenu(event: any) {
    event.stopPropagation();
    if (event.type === "ionScrollStart" && !this.menuIsShown) return;
    this.menuIsShown = !this.menuIsShown;
  }

  getCurrentLangDates(){
    const currentLang = this.translate.currentLang;
    switch (currentLang){
      case 'va': return 'ca';
      default: return currentLang;
    }
  }

  //------------------------------------------ AUXILIAR DISTANCE FUNCTIONS ------------------------------------------------------------
    sortItemsPerDistance(items, location) {
      return items?.map((item) => {
        item.distance = this.getDistanceBetween(parseFloat(location.latitude), parseFloat(location.longitude), parseFloat(item.latitude), parseFloat(item.longitude));
        item.distanceM = item.distance >= 1000 ? (item.distance / 1000).toFixed(2) + " Km's" : item.distance.toFixed(0) + " M's";
        return item;
      }).sort((a, b) => a.distance - b.distance);
    }
  
    getDistanceBetween(lat1: any, lon1: any, lat2: any, lon2: any): any {
      const rad = function (x) { return (x * Math.PI) / 180 };
      const R = 6378.137;
      const dLat = rad(lat2 - lat1);
      const dLong = rad(lon2 - lon1);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +  Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const d = R * c * 1000;
      return d;
    }
  
}